import React, { memo, useMemo } from 'react';
import { ThumbsUp } from 'lucide-react';
import './OrderStatusModal.css';

function vencKindOf(r) {
  const v = r?.contratoVenc;
  if (!v || !v.dateLabel) return 'none';
  return v.expired ? 'expired' : 'soon';
}

function buildGroupedItems(results) {
  const list = Array.isArray(results) ? results : [];
  const sorted = list.slice().sort((a, b) => {
    const aOk = a?.status === 'success';
    const bOk = b?.status === 'success';
    if (aOk === bOk) return 0;
    return aOk ? 1 : -1;
  });

  const groupMap = new Map();
  sorted.forEach((r) => {
    const ok = r?.status === 'success';
    const mensagem = ok
      ? `Movida para "${r.targetSection || 'Em Aberto'}"`
      : (r?.errorMessage || 'Erro desconhecido');
    const vencKind = vencKindOf(r);
    const key = `${ok ? '1' : '0'}|${mensagem}|${vencKind}`;
    if (!groupMap.has(key)) {
      groupMap.set(key, { ok, mensagem, vencKind, entries: [] });
    }
    groupMap.get(key).entries.push(r);
  });

  return Array.from(groupMap.values()).map((g) => {
    const dates = [
      ...new Set(
        g.entries
          .map((e) => e?.contratoVenc?.dateLabel)
          .filter(Boolean)
          .map(String)
      ),
    ];
    return {
      ok: g.ok,
      mensagem: g.mensagem,
      vencKind: g.vencKind,
      count: g.entries.length,
      countLabel: g.entries.length > 1 ? `${g.entries.length}x` : '',
      numerosLabel: g.entries
        .map((e) => String(e.orderId ?? '').trim())
        .filter(Boolean)
        .join(', '),
      dateLabels: dates,
    };
  });
}

function tecnicoInitial(name) {
  const s = String(name || '').trim();
  return s ? s.charAt(0).toUpperCase() : '?';
}

function singleContratoNotice(results) {
  if (!Array.isArray(results) || results.length !== 1) return null;
  const v = results[0]?.contratoVenc;
  if (!v || !v.dateLabel) return null;
  return {
    title: v.expired ? 'Contrato vencido' : 'Contrato prestes a vencer',
    prefix: v.expired
      ? 'O contrato deste equipamento já está vencido. Data de vencimento: '
      : 'O contrato deste equipamento vence em 30 dias ou menos. Data de vencimento: ',
    dateLabel: v.dateLabel,
    expired: Boolean(v.expired),
  };
}

function StatusItemRow({ item, showVencDate }) {
  return (
    <div className={`order-status-item ${item.ok ? 'is-success' : 'is-error'}`}>
      <div className="order-status-item-icon" aria-hidden>
        {item.ok ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="currentColor" className="order-status-item-icon-bg" />
            <path
              d="M8 12.5l2.5 2.5L16 9"
              stroke="currentColor"
              className="order-status-item-icon-mark"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="currentColor" className="order-status-item-icon-bg" />
            <path
              d="M12 7v6M12 16.5v.01"
              stroke="currentColor"
              className="order-status-item-icon-mark"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        )}
      </div>
      <div className="order-status-item-body">
        <div className="order-status-item-title-row">
          <span className="order-status-item-msg">{item.mensagem}</span>
          {item.countLabel ? (
            <span className={`order-status-item-badge ${item.ok ? 'is-success' : 'is-error'}`}>
              {item.countLabel}
            </span>
          ) : null}
        </div>
        {item.numerosLabel ? (
          <div className="order-status-item-os">{item.numerosLabel}</div>
        ) : null}
        {showVencDate && item.vencKind !== 'none' && item.dateLabels?.length ? (
          <div className="order-status-item-venc-date">
            Vencimento: <strong>{item.dateLabels.join(', ')}</strong>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Modal de resultado da movimentação de OS no Board (sucesso / erro / misto).
 */
function OrderStatusModal({ isOpen, results = [], summary = null, onClose, onRetry }) {
  const items = useMemo(() => buildGroupedItems(results), [results]);
  const successCount = useMemo(
    () => (Array.isArray(results) ? results.filter((r) => r.status === 'success').length : 0),
    [results]
  );
  const errorCount = useMemo(
    () => (Array.isArray(results) ? results.filter((r) => r.status === 'error').length : 0),
    [results]
  );
  const singleNotice = useMemo(() => singleContratoNotice(results), [results]);
  const isMulti = Array.isArray(results) && results.length > 1;

  const sections = useMemo(() => {
    if (!isMulti) {
      return [{ kind: 'none', title: null, items }];
    }
    const expired = items.filter((i) => i.vencKind === 'expired');
    const soon = items.filter((i) => i.vencKind === 'soon');
    const none = items.filter((i) => i.vencKind === 'none');
    const out = [];
    if (expired.length) out.push({ kind: 'expired', title: 'Contrato vencido', items: expired });
    if (soon.length) out.push({ kind: 'soon', title: 'Contrato prestes a vencer', items: soon });
    if (none.length) out.push({ kind: 'none', title: null, items: none });
    return out;
  }, [isMulti, items]);

  if (!isOpen) return null;

  const total = Array.isArray(results) ? results.length : 0;
  const headerSubtitle =
    total === 1
      ? '1 ordem de serviço processada'
      : `${total} ordens de serviço processadas`;

  const techName = summary?.technicianName && summary.technicianName !== 'N/A'
    ? String(summary.technicianName)
    : '';
  const secao = summary?.targetSection ? String(summary.targetSection) : '';
  const showContext = Boolean(secao || techName);

  const handleRetry = () => {
    if (typeof onRetry === 'function') onRetry();
    else if (typeof onClose === 'function') onClose();
  };

  return (
    <div className="modal-overlay order-status-modal-overlay" role="presentation">
      <div
        className="order-status-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-status-modal-subtitle"
      >
        <div className="order-status-header">
          <div className="order-status-header-text">
            <div id="order-status-modal-subtitle" className="order-status-subtitle">
              {headerSubtitle}
            </div>
          </div>
        </div>

        <div className="order-status-pills">
          {successCount > 0 && (
            <div className="order-status-pill order-status-pill--success">
              <span className="order-status-pill-icon" aria-hidden>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="currentColor" className="order-status-pill-icon-bg" />
                  <path
                    d="M8 12.5l2.5 2.5L16 9"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {successCount} sucesso
            </div>
          )}
          {errorCount > 0 && (
            <div className="order-status-pill order-status-pill--error">
              <span className="order-status-pill-icon" aria-hidden>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="currentColor" className="order-status-pill-icon-bg" />
                  <path
                    d="M12 7v6M12 16.5v.01"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              {errorCount} erro{errorCount === 1 ? '' : 's'}
            </div>
          )}
        </div>

        {showContext && (
          <div className="order-status-context">
            {secao && (
              <div className="order-status-context-row">
                <span className="order-status-context-label">Seção:</span>
                <span className="order-status-context-value">{secao}</span>
              </div>
            )}
            {techName && (
              <div className="order-status-context-row order-status-context-row--tech">
                <span className="order-status-context-label">Técnico:</span>
                <span className="order-status-tech">
                  <span className="order-status-tech-avatar" aria-hidden>
                    {tecnicoInitial(techName)}
                  </span>
                  <span className="order-status-tech-name">{techName}</span>
                </span>
              </div>
            )}
          </div>
        )}

        <div className="order-status-content">
          <div className="order-status-list">
            {sections.map((sec) => (
              <div
                key={sec.kind + (sec.title || 'plain')}
                className={`order-status-section${sec.kind !== 'none' ? ` is-${sec.kind}` : ''}`}
              >
                {sec.title ? (
                  <div className={`order-status-section-title is-${sec.kind}`}>{sec.title}</div>
                ) : null}
                {sec.items.map((item) => (
                  <StatusItemRow
                    key={`${item.ok ? 'ok' : 'err'}-${item.mensagem}-${item.vencKind}-${item.numerosLabel}`}
                    item={item}
                    showVencDate={isMulti}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        {singleNotice ? (
          <div
            className={`order-status-contrato-notice${singleNotice.expired ? ' is-expired' : ' is-soon'}`}
          >
            <div className="order-status-contrato-notice-title">{singleNotice.title}</div>
            <p className="order-status-contrato-notice-text">
              {singleNotice.prefix}
              <strong>{singleNotice.dateLabel}</strong>.
            </p>
          </div>
        ) : null}

        <div className="order-status-footer">
          {errorCount > 0 && (
            <button type="button" className="order-status-retry-btn" onClick={handleRetry}>
              Tentar novamente
            </button>
          )}
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn order-status-entendi-btn"
            onClick={onClose}
          >
            <ThumbsUp size={14} strokeWidth={2.25} aria-hidden />
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(OrderStatusModal);
